/**
 * deviceManager — stable device identity for the Copilot PWA.
 *
 * Generates a UUID on first load and persists it across page reloads using
 * a dual-storage strategy (localStorage + cookie), so the token survives
 * clearing one storage but not the other.
 *
 * If both storages are cleared (e.g. full browser data wipe), a new token is
 * generated automatically. The backend will treat this as a new device.
 *
 * For future native Capacitor builds: replace getOrCreateToken() with
 * Capacitor's Device.getId() which returns a stable OS-level hardware ID.
 */

import { cookieManager } from '@/service/cookieManager';

const DEVICE_TOKEN_KEY = 'copilotDeviceToken';

class DeviceManager {
    constructor() {
        /** @type {string|null} In-memory cache — avoids repeated storage reads */
        this._token = null;
    }

    /**
     * Returns the stable device token for this browser/device.
     * Creates and persists a new UUID if neither storage has one.
     *
     * @returns {string}
     */
    getOrCreateToken() {
        if (this._token) return this._token;

        const existing = this._readFromStorage();
        if (existing) {
            // Restore whichever storage was missing (e.g. after a partial clear)
            this._writeToStorage(existing);
            this._token = existing;
            return this._token;
        }

        const newToken = crypto.randomUUID();
        this._writeToStorage(newToken);
        this._token = newToken;
        return this._token;
    }

    /**
     * Collects device info to send on registration.
     * Auto-detects platform and OS from userAgent; screen dimensions from screen API.
     * Model and manufacturer are always null for PWA (only available via Capacitor native).
     *
     * @returns {object}
     */
    getDeviceInfo() {
        const token = this.getOrCreateToken();
        const userAgent = navigator.userAgent || '';

        let platform = 'web';
        let os = 'Unknown';

        if (/android/i.test(userAgent)) {
            platform = 'android';
            const match = userAgent.match(/Android ([0-9.]+)/);
            os = match ? `Android ${match[1]}` : 'Android';
        } else if (/iPad|iPhone|iPod/i.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
            platform = 'ios';
            const match = userAgent.match(/OS ([0-9_]+)/);
            os = match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS';
        } else if (/Win/i.test(userAgent)) {
            os = 'Windows';
        } else if (/Mac/i.test(userAgent)) {
            os = 'macOS';
        } else if (/Linux/i.test(userAgent)) {
            os = 'Linux';
        }

        return {
            deviceToken: token,
            platform,
            os,
            model: null,
            manufacturer: null,
            appVersion: process.env.VUE_APP_VERSION || null,
            screenWidth: window.screen?.width || null,
            screenHeight: window.screen?.height || null,
        };
    }

    // -------------------------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------------------------

    _readFromStorage() {
        // localStorage first — fastest and most reliable when intact
        try {
            const fromLocal = localStorage.getItem(DEVICE_TOKEN_KEY);
            if (fromLocal) return fromLocal;
        } catch (_e) {
            // localStorage blocked (e.g. strict privacy settings) — fall through
        }

        // Cookie fallback — survives localStorage clears
        const fromCookie = cookieManager.getCookie(DEVICE_TOKEN_KEY);
        return fromCookie || null;
    }

    _writeToStorage(token) {
        // cookieManager.setValue writes to both localStorage AND cookie (if consent given)
        cookieManager.setValue(DEVICE_TOKEN_KEY, token);
    }
}

export const deviceManager = new DeviceManager();
